import { useState, useEffect, useCallback } from 'react'
import { GitCommit, RefreshCw, Newspaper, Shield, Cloud, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

const SOURCE_COLORS: Record<string, string> = {
  'Hacker News': '#ff6600',
  'Ars Technica': '#ff4444',
  'TechCrunch': '#0a9c22',
  'The Verge': '#e5127d',
  'Wired': '#000000',
  'Reddit': '#ff4500',
  'GitHub': '#8b5cf6',
}

const BAR_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

export default function WhatChangedPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [cloudStatus, setCloudStatus] = useState<any[]>([])
  const [movers, setMovers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [newsRes, threatRes, cloudRes, moversRes] = await Promise.allSettled([
        api.get('/api/news/feed', { params: { limit: 100 } }),
        api.get('/api/cyber/threat-level'),
        api.get('/api/cloud/status'),
        api.get('/api/trends/top-movers'),
      ])
      if (newsRes.status === 'fulfilled') setArticles(newsRes.value.data.articles || newsRes.value.data || [])
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      if (cloudRes.status === 'fulfilled') setCloudStatus(cloudRes.value.data.providers || [])
      if (moversRes.status === 'fulfilled') setMovers(moversRes.value.data.movers || moversRes.value.data || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Filter articles from last 24 hours
  const cutoff = Date.now() - 24 * 60 * 60 * 1000
  const todayArticles = articles.filter(a => {
    try { return new Date(a.published_at || a.timestamp || a.date).getTime() > cutoff } catch { return false }
  })

  // Source breakdown
  const sourceMap: Record<string, number> = {}
  todayArticles.forEach(a => {
    const src = a.source || 'Unknown'
    sourceMap[src] = (sourceMap[src] || 0) + 1
  })
  const sourceBreakdown = Object.entries(sourceMap)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }))

  // Cloud providers with non-operational status
  const degradedProviders = cloudStatus.filter(p => p.overall_status && p.overall_status !== 'operational')

  // Threat level
  const prevThreat = typeof window !== 'undefined' ? localStorage.getItem('ctodash_prev_threat') : null
  const currentThreat = threatLevel?.level || threatLevel?.threat_level
  if (currentThreat && typeof window !== 'undefined') {
    localStorage.setItem('ctodash_prev_threat', currentThreat)
  }
  const threatChanged = prevThreat && currentThreat && prevThreat !== currentThreat

  const threatColors: Record<string, string> = {
    low: 'text-emerald',
    medium: 'text-amber',
    high: 'text-orange',
    elevated: 'text-orange',
    critical: 'text-rose',
    severe: 'text-rose',
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <GitCommit size={20} className="text-cyan" /> What Changed Today
          </h1>
          <p className="text-[10px] text-text-secondary">Your morning catch-up — what happened in the last 24 hours</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* ── New Today ──────────────────────────────────────────── */}
          <div className="card p-4 lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Newspaper size={14} className="text-blue" />
              <h2 className="text-sm font-semibold text-text-primary">New Today</h2>
              <span className="bg-blue/10 text-blue text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                {todayArticles.length} articles
              </span>
            </div>
            {sourceBreakdown.length > 0 ? (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sourceBreakdown} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <Tooltip
                      contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 6, fontSize: 11 }}
                      labelStyle={{ color: '#e2e8f0' }}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {sourceBreakdown.map((_, i) => (
                        <Cell key={i} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-[11px] text-text-muted">No new articles in the last 24 hours.</p>
            )}
          </div>

          {/* ── Threat Level ───────────────────────────────────────── */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={14} className="text-rose" />
              <h2 className="text-sm font-semibold text-text-primary">Threat Level</h2>
            </div>
            {currentThreat ? (
              <div>
                <div className="flex items-center gap-3">
                  <span className={clsx('text-2xl font-bold capitalize', threatColors[currentThreat.toLowerCase()] || 'text-text-primary')}>
                    {currentThreat}
                  </span>
                  {threatChanged && (
                    <span className="bg-amber/10 text-amber text-[10px] px-2 py-0.5 rounded-full font-semibold">
                      Changed from {prevThreat}
                    </span>
                  )}
                </div>
                {threatLevel?.summary && (
                  <p className="text-[11px] text-text-secondary mt-2 leading-relaxed">{threatLevel.summary}</p>
                )}
                {!threatChanged && prevThreat && (
                  <p className="text-[10px] text-text-muted mt-2">No change since last check.</p>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-text-muted">Threat level data unavailable.</p>
            )}
          </div>

          {/* ── Cloud Status Changes ───────────────────────────────── */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cloud size={14} className="text-cyan" />
              <h2 className="text-sm font-semibold text-text-primary">Cloud Status</h2>
            </div>
            {degradedProviders.length > 0 ? (
              <div className="space-y-2">
                {degradedProviders.map(p => (
                  <div key={p.slug || p.name} className="flex items-center justify-between bg-surface-raised p-2 rounded">
                    <span className="text-xs font-medium text-text-primary">{p.name}</span>
                    <span className={clsx(
                      'text-[10px] font-semibold px-1.5 py-0.5 rounded capitalize',
                      p.overall_status === 'degraded' && 'bg-amber/10 text-amber',
                      p.overall_status === 'outage' && 'bg-rose/10 text-rose',
                      p.overall_status === 'maintenance' && 'bg-blue/10 text-blue',
                    )}>
                      {p.overall_status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
                <p className="text-[11px] text-emerald font-medium">All cloud providers operational</p>
              </div>
            )}
          </div>

          {/* ── Trending Movers ─────────────────────────────────────── */}
          <div className="card p-4 lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-violet" />
              <h2 className="text-sm font-semibold text-text-primary">Trending Movers</h2>
              <span className="text-[10px] text-text-muted">Entities with biggest mention changes</span>
            </div>
            {movers.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                {movers.slice(0, 12).map((m: any, i: number) => {
                  const change = m.change ?? m.delta ?? 0
                  const isUp = change > 0
                  return (
                    <div key={m.name || i} className="bg-surface-raised p-2.5 rounded border border-border">
                      <div className="text-xs font-semibold text-text-primary truncate">{m.name || m.entity}</div>
                      <div className="flex items-center gap-1 mt-1">
                        {isUp ? (
                          <ArrowUpRight size={12} className="text-emerald" />
                        ) : (
                          <ArrowDownRight size={12} className="text-rose" />
                        )}
                        <span className={clsx('text-[11px] font-bold', isUp ? 'text-emerald' : 'text-rose')}>
                          {isUp ? '+' : ''}{change}
                        </span>
                        <span className="text-[9px] text-text-muted">mentions</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-[11px] text-text-muted">No mover data available yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
